import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus, Trash2, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Spinner, Field, Input, Select, Textarea } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// BidDeliverablesCard — plan the deliverables/logistics you'll need at BID
// stage (plant / materials / delivery / electrical …). A lightweight list, no
// firm dates required. When the bid is won → promoted, these seed the project's
// bookings (status 'tentative') via the seed trigger (migration 195), so they
// carry through to the project deliverables board.
// =============================================================================

const TYPES: { value: string; label: string }[] = [
  { value: "plant", label: "Plant" },
  { value: "materials", label: "Materials" },
  { value: "delivery", label: "Delivery" },
  { value: "electrical", label: "Electrical" },
  { value: "integration", label: "Integration" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "inspection", label: "Inspection" },
  { value: "meeting", label: "Meeting" },
  { value: "outage", label: "Outage" },
  { value: "other", label: "Other" },
];
const TYPE_LABEL: Record<string, string> = Object.fromEntries(TYPES.map(t => [t.value, t.label]));

interface Deliverable {
  deliverable_id: string; deliverable_type: string; title: string; notes: string | null; est_date: string | null;
}

export default function BidDeliverablesCard({ bidId, canEdit }: { bidId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const user = useRequireAuth();
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState("plant");
  const [title, setTitle] = useState("");
  const [estDate, setEstDate] = useState("");
  const [notes, setNotes] = useState("");

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["bid_deliverables", bidId],
    queryFn: async () => {
      const { data } = await supabase.from("bid_deliverables")
        .select("deliverable_id, deliverable_type, title, notes, est_date")
        .eq("bid_id", bidId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      return (data ?? []) as Deliverable[];
    },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      if (!title.trim()) return;
      const { error } = await supabase.from("bid_deliverables").insert({
        bid_id: bidId, deliverable_type: type, title: title.trim(),
        est_date: estDate || null, notes: notes.trim() || null, created_by: user?.user_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => { setTitle(""); setEstDate(""); setNotes(""); setAdding(false); qc.invalidateQueries({ queryKey: ["bid_deliverables", bidId] }); },
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("bid_deliverables").delete().eq("deliverable_id", id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bid_deliverables", bidId] }),
  });

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={14} className="text-text-muted" />
            <span className="text-[12px] uppercase tracking-wider text-text-muted">Deliverables to line up</span>
            {items.length > 0 && <span className="text-[11px] text-text-muted">· {items.length}</span>}
          </div>
          {canEdit && !adding && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>Add</Button>}
        </div>

        {isLoading && <div className="flex justify-center py-3"><Spinner size={14} /></div>}

        {!isLoading && items.length === 0 && !adding && (
          <div className="text-[12px] text-text-muted py-1">
            Plan the plant, materials, delivery and electrical you'll need. When this bid is won they carry into the project's bookings automatically.
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div className="space-y-1.5">
            {items.map(d => (
              <div key={d.deliverable_id} className="group flex items-center gap-2 bg-ink rounded border border-line/40 px-3 py-2 text-[12px]">
                <span className="text-[10px] font-mono uppercase tracking-wider2 text-accent bg-accent/10 rounded px-1.5 py-0.5 flex-shrink-0">{TYPE_LABEL[d.deliverable_type] ?? d.deliverable_type}</span>
                <span className="text-text truncate flex-1 min-w-0">{d.title}{d.notes && <span className="text-text-dim"> · {d.notes}</span>}</span>
                {d.est_date && <span className="text-text-muted font-mono text-[11px] flex-shrink-0">~{fmtDate(d.est_date)}</span>}
                {canEdit && (
                  <button type="button" onClick={() => delMut.mutate(d.deliverable_id)} className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-lost p-0.5" title="Remove">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {adding && canEdit && (
          <div className="mt-3 border-t border-line pt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Type">
                <Select value={type} onChange={e => setType(e.target.value)}>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </Field>
              <Field label="Est. date" hint="Optional">
                <Input type="date" value={estDate} onChange={e => setEstDate(e.target.value)} />
              </Field>
            </div>
            <Field label="What's needed">
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 20T franna crane, 40m³ concrete, NBN electrical connection" />
            </Field>
            <Field label="Notes" hint="Optional">
              <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
            </Field>
            <div className="text-[10px] text-text-muted italic inline-flex items-center gap-1">
              <ArrowRight size={11} /> Carries into the project's bookings when this bid is won.
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={!title.trim() || addMut.isPending} onClick={() => addMut.mutate()}>{addMut.isPending ? "Adding…" : "Add deliverable"}</Button>
              <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
